import { useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Collapse from '@zenbusiness/zui/Collapse';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';

import { FeaturedProducts } from './FeaturedProducts';
import { SubscriptionAsset } from './SubscriptionAsset';
import { SubscriptionDetails } from './SubscriptionDetails';
import { SubscriptionMenu } from './SubscriptionMenu';
import { SubscriptionPrice } from './SubscriptionPrice';
import {
  SERVICE_ACTIVATION_LINK_TEXT,
  SERVICE_ACTIVATION_PATH_SUFFIXES,
  WEBSITE_TOOLS_BUNDLE_INCLUDES_ORDER,
  WEBSITE_TOOLS_BUNDLE_SERVICES
} from '../constants';
import ChangeRequestContext from '../contexts/ChangeRequestContext';
import ChangeBillingIntervalAnnualModalFlow from '../modals/ChangeBillingIntervalAnnualModalFlow';
import RestartSubscriptionModalFlow from '../modals/RestartSubscriptionModalFlow';
import getClaimedPromotionalItemsInTerm from '../utils/getClaimedPromotionalItems';
import getServiceVersionless from '../utils/getServiceVersionless';
import translateSubscriptionStatus from '../utils/translateSubscriptionStatus';

function lookupByService(map, service) {
  if (!map || !service) return undefined;
  return map[service] ?? map[getServiceVersionless(service)];
}

export function SubscriptionRow({
  subscription,
  businessEntity,
  serviceProductsMap,
  productsMap,
  refetchSubscriptions
}) {
  const { formatMessage } = useIntl();
  const [checked, setChecked] = useState(true);

  const serviceName = subscription?.service || '';
  const activationSuffix = SERVICE_ACTIVATION_PATH_SUFFIXES[serviceName];
  const topLevelActivationPath = activationSuffix
    ? `/dashboard/${businessEntity?.uuid}${activationSuffix}`
    : null;
  const topLevelActivationLinkText = SERVICE_ACTIVATION_LINK_TEXT[serviceName];

  const includedProducts = useMemo(() => {
    if (!serviceProductsMap || !subscription?.includes) return [];

    const products = subscription.includes
      .map((included) => ({
        ...lookupByService(serviceProductsMap, included.service),
        ...included
      }))
      .filter((product) => product.service);

    if (WEBSITE_TOOLS_BUNDLE_SERVICES.includes(serviceName)) {
      const order = WEBSITE_TOOLS_BUNDLE_INCLUDES_ORDER;
      return products.sort((a, b) => {
        const aOrder = order[a.service] ?? Infinity;
        const bOrder = order[b.service] ?? Infinity;
        return aOrder - bOrder;
      });
    }

    return products;
  }, [serviceProductsMap, subscription?.includes, serviceName]);

  const mainService = useMemo(() => {
    if (!serviceProductsMap || !serviceName) return null;
    return lookupByService(serviceProductsMap, serviceName) || null;
  }, [serviceProductsMap, serviceName]);

  const products = useMemo(() => {
    if (!productsMap || !serviceName) return [];
    return lookupByService(productsMap, serviceName) || [];
  }, [productsMap, serviceName]);

  if (!serviceProductsMap || !productsMap) {
    return (
      <Skeleton
        animation="wave"
        variant="rounded"
        sx={{ width: '100%', height: 200, marginBlock: '0.75rem' }}
      />
    );
  }

  const serviceDisplayName = mainService?.displayName || serviceName;

  const {
    subscriptionStatus: status,
    price,
    termEndDate,
    payment,
    cancelDate,
    contractInterval,
    period,
    summary,
    benefits,
    inTrial,
    termStartDate
  } = subscription;

  const paymentStatus = payment?.[0]?.paymentStatus || '';
  const message = translateSubscriptionStatus({
    formatMessage,
    status,
    paymentStatus,
    termEndDate,
    cancelDate,
    inTrial,
    termStartDate,
    service: serviceName,
    price,
    period
  });

  const useAltStyles = ['cancel_renewal'].includes(status);
  const shouldShowIncludes = includedProducts?.length > 0;

  const promotion = getClaimedPromotionalItemsInTerm(
    subscription.promotions,
    subscription.termEndDate
  );

  const priceMo = products?.find((p) => p.pricing?.interval === 'monthly')?.pricing?.amount;
  const priceYr = products?.find((p) => p.pricing?.interval === 'annual')?.pricing?.amount;
  // Savings formula: monthly price / annual price * 100
  const billingChangeSavings = priceMo && priceYr ? parseInt((priceMo / priceYr) * 100, 10) : 0;

  const oneYearCommitment = serviceName.includes('tax_advice')
    && period === 'monthly'
    && contractInterval === 'annual';

  const hasIncludes = includedProducts.length > 0;

  return (
    <Box sx={{ marginBlock: '0.75rem' }}>
      <ChangeRequestContext>
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '1rem',
            borderBottomLeftRadius: checked && hasIncludes ? 0 : '1rem',
            borderBottomRightRadius: checked && hasIncludes ? 0 : '1rem',
            padding: '1.5rem',
            backgroundColor: hasIncludes
              ? theme.palette.action.hover
              : theme.palette.background.paper,
            display: 'grid',
            columnGap: '1.5rem',
            alignItems: 'center',
            gridTemplateColumns: hasIncludes ? 'auto min-content' : 'max-content auto min-content',
            [theme.breakpoints.down('md')]: {
              gridTemplateRows: '1fr',
              gridTemplateColumns: 'auto min-content'
            }
          })}
        >
          <SubscriptionAsset
            service={serviceName}
            included={hasIncludes}
          />
          <Stack
            direction="column"
            justifyContent="space-between"
            gap={4}
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: {
                flexDirection: 'row',
                alignItems: 'center'
              },
              [theme.breakpoints.down('md')]: {
                alignItems: 'flex-start',
                gridRow: hasIncludes ? 1 : 2,
                gridColumn: hasIncludes ? '1' : '1/-1'
              }
            })}
          >
            <SubscriptionDetails
              serviceName={serviceDisplayName}
              summary={summary}
              benefits={benefits}
              included={hasIncludes}
              inTrial={inTrial}
              partnerDomain={subscription?.partnerDomain ?? null}
              activationPath={hasIncludes ? null : topLevelActivationPath}
              activationLinkText={hasIncludes ? null : topLevelActivationLinkText}
            />
            <SubscriptionPrice
              subscription={subscription}
              price={price}
              promotionalItem={promotion}
              useAltStyles={useAltStyles}
              oneYearCommitment={oneYearCommitment}
              message={message}
            />
          </Stack>
          <SubscriptionMenu
            subscription={subscription}
            billingChangeSavings={billingChangeSavings}
            domesticJurisdiction={businessEntity?.domesticJurisdiction}
            hasSubProducts={hasIncludes}
            checked={checked}
            setChecked={setChecked}
            businessEntityUuid={businessEntity?.uuid}
          />
        </Box>

        {shouldShowIncludes && (
          <Collapse in={checked}>
            <Box
              sx={(theme) => ({
                width: '100%',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '1rem',
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0
              })}
            >
              <FeaturedProducts
                includedProducts={includedProducts}
                businessEntityUuid={businessEntity?.uuid}
              />
            </Box>
          </Collapse>
        )}

        <RestartSubscriptionModalFlow
          subscription={subscription}
          businessEntity={businessEntity}
          serviceDisplayName={serviceDisplayName}
          refetchSubscriptions={refetchSubscriptions}
        />
        <ChangeBillingIntervalAnnualModalFlow
          subscription={subscription}
          businessEntity={businessEntity}
          serviceDisplayName={serviceDisplayName}
          products={products}
          billingChangeSavings={billingChangeSavings}
          refetchSubscriptions={refetchSubscriptions}
        />
      </ChangeRequestContext>
    </Box>
  );
}

SubscriptionRow.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    partnerDomain: PropTypes.shape({
      domainName: PropTypes.string
    }),
    service: PropTypes.string,
    subscriptionStatus: PropTypes.string,
    price: PropTypes.number,
    period: PropTypes.string,
    canChangePeriod: PropTypes.bool,
    inTrial: PropTypes.bool,
    termStartDate: PropTypes.string,
    termEndDate: PropTypes.string,
    cancelDate: PropTypes.string,
    contractInterval: PropTypes.string,
    summary: PropTypes.string,
    benefits: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string
    })),
    payment: PropTypes.arrayOf(PropTypes.shape({
      paymentStatus: PropTypes.string
    })),
    includes: PropTypes.arrayOf(PropTypes.shape({
      service: PropTypes.string
    })),
    promotions: PropTypes.arrayOf(PropTypes.shape({})),
    remainingProrateValue: PropTypes.number,
    isFormationComplete: PropTypes.bool,
    isPostPurchaseComplete: PropTypes.bool
  }).isRequired,
  businessEntity: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    domesticJurisdiction: PropTypes.string.isRequired
  }).isRequired,
  serviceProductsMap: PropTypes.objectOf(PropTypes.shape({
    displayName: PropTypes.string,
    service: PropTypes.string
  })),
  productsMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.shape({
    service: PropTypes.string,
    pricing: PropTypes.shape({
      interval: PropTypes.string,
      amount: PropTypes.number
    })
  }))),
  refetchSubscriptions: PropTypes.func
};
